import { getStates, hasDeliveredArtWorkService } from '@printdeal/business-logic';
import { useQuery } from '@tanstack/react-query';
import { ErrorManagement } from '@printdeal/error-management/react';
import { SanitizedOrder } from '../../../../../../types/order/Order';
import { Environment } from '../../../../../../helpers/env';
// @ts-expect-error
import { isPayable, canChangePaymentMethod } from '../../../../../../helpers/order/Order';
// @ts-expect-error
import { getPaymentMethod, paymentMethodIds } from '../../../../../../helpers/payments/paymentMethods';
import { ServiceFactory } from '../../../../../../services/ServiceFactory';
import { dispatchAccountEvent } from '../../../../../../helpers/dataLayerHelpers/accountDataLayerHelper';

const ReasonsDisabled = {
  ORDER_UNPAYABLE: 'Order cannot be payed',
  PAYMENT_METHOD_LOCKED: 'The payment method is locked due to lineItem states',
  PAYMENT_METHOD_CANT_BE_CHANGED: 'The payment method can\'t be changed due to service states',
  GET_METHODS_ERROR: 'Could not retrieve the list of payment methods.',
};

const useChangePaymentMethod = (order: SanitizedOrder) => {
  const states = getStates(Environment);
  const paymentMethods = useQuery({
    queryKey: ['order', 'get-payment-methods', order.id],
    queryFn: async () => {
      try {
        const PaymentMethodsQuery = await ServiceFactory.createPaymentV2Service()
          .getPaymentMethodsForOrder(order.id);

        return PaymentMethodsQuery.methods;
      } catch (error) {
        ErrorManagement.exception({
          exception: error,
          tags: {
            fileLocation: 'ChangePaymentMethod',
            featureFunction: 'useChangePaymentMethod',
          },
          fingerprint: ['order-detail-page-get-payment-methods-in-hook'],
        });

        dispatchAccountEvent('order_detail_api_failure', 'get_payment_methods');
        return [];
      }
    },
    enabled: isPayable(order, states, order.lineItems)
      && canChangePaymentMethod(states, order.lineItems)
      && !hasDeliveredArtWorkService(order, Environment),
  });

  if (hasDeliveredArtWorkService(order, Environment)
    && getPaymentMethod(order.paymentInfo) === paymentMethodIds.onAccount) {
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.PAYMENT_METHOD_CANT_BE_CHANGED,
      paymentMethods: [],
    };
  }

  if (!isPayable(order, states, order.lineItems)) {
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.ORDER_UNPAYABLE,
      paymentMethods: [],
    };
  }

  if (!canChangePaymentMethod(states, order.lineItems)) {
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.PAYMENT_METHOD_LOCKED,
      paymentMethods: [],
    };
  }

  if (!paymentMethods.data || paymentMethods.data.length === 0) {
    return {
      enabled: false,
      reasonDisabled: ReasonsDisabled.GET_METHODS_ERROR,
      paymentMethods: [],
    };
  }

  return {
    enabled: true,
    paymentMethods: paymentMethods.data,
  };
};

export default useChangePaymentMethod;
